import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { adsAPI } from '../api';
import { useCompany } from './CompanyContext';

const AdContext = createContext(null);
const AD_INTERVAL_MS = 5 * 60 * 1000;

export function AdProvider({ children }) {
    const { company } = useCompany();
    const [ads, setAds] = useState([]);
    const [current, setCurrent] = useState(null);
    const [visible, setVisible] = useState(false);
    const lastShownAt = useRef(0);
    const pendingShow = useRef(null);

    useEffect(() => {
        if (!company?.companyId) return;
        adsAPI.listActive(company.companyId)
            .then(({ data }) => {
                const companyAds = (data.ads || []).filter(
                    ad => !ad.companyId || ad.companyId === company.companyId
                );
                setAds(companyAds);
            })
            .catch(() => { });
    }, [company?.companyId]);

    const getEligibleAds = useCallback((targetPage) => {
        return ads.filter(ad => {
            if (!ad.mediaUrl) return false;
            if (targetPage && ad.targetPages?.length > 0) return ad.targetPages.includes(targetPage);
            return true;
        });
    }, [ads]);

    const showAd = useCallback((targetPage = null) => {
        const eligible = getEligibleAds(targetPage);
        if (!eligible.length) return;
        const ad = eligible[Math.floor(Math.random() * eligible.length)];
        lastShownAt.current = Date.now();
        setCurrent(ad);
        setVisible(true);
        adsAPI.recordImpression(ad.adId).catch(() => { });
    }, [getEligibleAds]);

    useEffect(() => {
        if (ads.length === 0) return;
        if (pendingShow.current !== null) {
            const page = pendingShow.current;
            pendingShow.current = null;
            showAd(page);
        } else if (lastShownAt.current === 0) {
            showAd(null);
        }
    }, [ads, showAd]);

    const maybeShowAd = useCallback((targetPage = null) => {
        if (Date.now() - lastShownAt.current < AD_INTERVAL_MS) return;
        if (!ads.length) { pendingShow.current = targetPage; return; }
        showAd(targetPage);
    }, [ads, showAd]);

    const dismissAd = useCallback((skipped = false) => {
        if (current && skipped) adsAPI.recordSkip(current.adId).catch(() => { });
        setVisible(false);
        setCurrent(null);
    }, [current]);

    const clickAd = useCallback(() => {
        if (current) adsAPI.recordClick(current.adId).catch(() => { });
    }, [current]);

    return (
        <AdContext.Provider value={{ ads, current, visible, maybeShowAd, dismissAd, clickAd }}>
            {children}
        </AdContext.Provider>
    );
}

export const useAd = () => useContext(AdContext);
